"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { NumericFormat } from "react-number-format";
import AlertDialog from "./AlertDialog";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "12px",
    ...(state.isDisabled && {
      backgroundColor: "#4b5563",
      opacity: 0.5,
      cursor: "not-allowed",
    }),
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "11px",
    padding: "1px 4px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": {
      backgroundColor: "#f97316",
      color: "#fff",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    zIndex: 50,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
    flexWrap: "wrap",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

export default function MaterialOrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = localStorage.getItem("userRole");
  // Form state
  const [formData, setFormData] = useState({
    purchaseOrder: "",
    vendor: "",
    poNumber: "",
    shippingMethod: "Delivery",
    deliveryInstructions: "",
    materialOrderNumber: "",
    contact: [],
    fieldContact: [],
    deliveryContact: null,
    expectedDeliveryDate: "",
    laborersRequired: false,
    laborersQty: 0,
  });

  // Data state
  const [project, setProject] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poItems, setPoItems] = useState([]); // Items linked to selected PO
  const [vendors, setVendors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [materialOrders, setMaterialOrders] = useState([]);
  const justSelectedRef = useRef(null);
  const qtyInputRefs = useRef({});
  // Selection state
  const [selectedItems, setSelectedItems] = useState([]);
  const [lockedVendor, setLockedVendor] = useState(null); // Vendor locked by first selection

  // Filter state (multi-select)
  // REPLACE WITH:
  const [filters, setFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    bidArea: [],
    laborId: [],
    size: [],
  });
  const [searchQuery, setSearchQuery] = useState("");

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [projectRes, posRes, vendorsRes, contactsRes, usersRes, mosRes] =
          await Promise.all([
            axios.get(`${API_BASE}/projects/${id}`, { headers }),
            axios.get(`${API_BASE}/purchaseOrders2?project=${id}`, { headers }),
            axios.get(`${API_BASE}/accounts/vendors`, { headers }),
            axios.get(`${API_BASE}/contacts`, { headers }),
            axios.get(`${API_BASE}/users`, { headers }),
            axios.get(`${API_BASE}/materialOrders2/project/${id}`, { headers }),
          ]);

        setProject(projectRes.data);
        setPurchaseOrders(posRes.data);
        setVendors(vendorsRes.data);
        setContacts(contactsRes.data);
        setUsers(usersRes.data);
        setMaterialOrders(mosRes.data);
      } catch (err) {
        setError(
          `Failed to load data: ${err.response?.data?.message || err.message}`,
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Fetch items when PO is selected — using NET quantities (matching PODetailView exactly)

  useEffect(() => {
    const fetchPOItems = async () => {
      if (!formData.purchaseOrder) {
        setPoItems([]);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        // Use PO-specific endpoint with excludeClosed=true to filter out closed items
        const netRes = await axios.get(
          `${API_BASE}/purchaseOrders2/${formData.purchaseOrder}/item-net-quantities?excludeClosed=true`,
          { headers },
        );

        const netItems = netRes.data.success ? netRes.data.items : [];

        // Helper to get vendor name safely using the vendors state
        const getVendorName = (vendorRef) => {
          if (!vendorRef) return "Unassigned";
          const vendorId =
            typeof vendorRef === "object" ? vendorRef._id : vendorRef;
          if (typeof vendorRef === "object" && vendorRef.companyName) {
            return vendorRef.companyName;
          }
          const vendor = vendors.find((v) => v._id === vendorId);
          return vendor?.companyName || "Unassigned";
        };

        // Map items for display
        // In the .map() inside fetchPOItems, replace the quantity mappings:

        const items = netItems
          .map((item) => ({
            ...item,
            assemblyId: item.assemblyId,
            lineItemId: item.lineItemId,
            vendorId: item.vendor?._id || item.vendor,
            vendorName: getVendorName(item.vendor),
            description: item.costCodeDescription || item.itemDescription || "",
            size: item.size || "-",
            bidArea: item.bidArea || "",
            conditionName: item.conditionName || "Uncategorized",
            isFullyOrdered: (item.remaining || 0) <= 0,
            sectionNumber: item.sectionNumber || "-",
            laborId: item.laborId || "-",
            requiredQty: Math.max(0, item.netRequiredQty || 0),
            qtyPending: Math.max(0, item.qtyPending || 0),
            qtyOrdered: Math.max(0, item.qtyOrdered || 0),
            qtyRemaining: Math.max(0, item.remaining || 0),
            amount: Math.max(0, item.amount || 0),
            unitPrice:
              item.price ||
              item.unitPrice ||
              item.cost ||
              (item.netRequiredQty > 0
                ? (item.amount || 0) / item.netRequiredQty
                : 0),
          }))
          // ← RIGHT HERE, between .map() and .sort()
          .filter((item) => {
            if (
              item.requiredQty <= 0 &&
              item.qtyRemaining <= 0 &&
              !item.isFullyOrdered
            ) {
              return false;
            }
            return true;
          })
          .sort((a, b) =>
            (a.conditionName || "").localeCompare(b.conditionName || ""),
          );

        setPoItems(items);
      } catch (err) {
        console.error("Error fetching net quantities for Material Order:", err);
        setError("Failed to load PO items. Please try again.");
        setPoItems([]);
      }
    };

    fetchPOItems();
  }, [formData.purchaseOrder, vendors]); // Add vendors as dependency
  // Filter items based on filters, search, and locked vendor

  const filteredItems = useMemo(() => {
    // Start with vendor lock filter
    let items = poItems.filter((item) => {
      if (lockedVendor && item.vendorId !== lockedVendor) return false;
      return true;
    });

    // Apply dropdown multi-select filters (exact match — driven by chips)
    if (filters.conditionName.length > 0)
      items = items.filter((item) =>
        filters.conditionName.includes(item.conditionName),
      );
    if (filters.sectionNumber.length > 0)
      items = items.filter((item) =>
        filters.sectionNumber.includes(item.sectionNumber),
      );
    if (filters.bidArea.length > 0)
      items = items.filter((item) => filters.bidArea.includes(item.bidArea));
    if (filters.laborId.length > 0)
      items = items.filter((item) => filters.laborId.includes(item.laborId));
    if (filters.size.length > 0)
      items = items.filter((item) => filters.size.includes(item.size));

    // Apply multi-token free-text search (ranked)
    if (searchQuery.trim()) {
      items = moMultiTokenSearch(items, searchQuery);
    }

    return items;
  }, [poItems, lockedVendor, searchQuery, filters]);
  // Get filter options from PO items
  const filterOptions = useMemo(() => {
    const conditions = new Set();
    const sections = new Set();
    const bidAreas = new Set();
    const laborIds = new Set();
    const sizes = new Set();

    poItems.forEach((item) => {
      if (item.conditionName) conditions.add(item.conditionName);
      if (item.sectionNumber) sections.add(item.sectionNumber);
      if (item.bidArea) bidAreas.add(item.bidArea);
      if (item.laborId && item.laborId !== "-") laborIds.add(item.laborId);
      if (item.size && item.size !== "-") sizes.add(item.size);
    });

    return {
      conditionName: Array.from(conditions)
        .sort()
        .map((v) => ({ value: v, label: v })),
      sectionNumber: Array.from(sections)
        .sort()
        .map((v) => ({ value: v, label: v })),
      bidArea: Array.from(bidAreas)
        .sort()
        .map((v) => ({ value: v, label: v })),
      laborId: Array.from(laborIds)
        .sort()
        .map((v) => ({ value: v, label: v })),
      size: Array.from(sizes)
        .sort()
        .map((v) => ({ value: v, label: v })),
    };
  }, [poItems]);

  // Group items by condition
  const groupedItems = useMemo(() => {
    const groups = {};
    filteredItems.forEach((item) => {
      const condition = item.conditionName || "Uncategorized";
      if (!groups[condition]) {
        groups[condition] = [];
      }
      groups[condition].push(item);
    });
    return groups;
  }, [filteredItems]);

  // Calculate total cost
  const totalCost = useMemo(() => {
    return selectedItems.reduce((sum, si) => {
      return sum + (si.quantity || 0) * (si.price || 0);
    }, 0);
  }, [selectedItems]);

  // PO options for dropdown
  const poOptions = useMemo(() => {
    return purchaseOrders.map((po) => ({
      value: po._id,
      label: `${po.title} (PO# ${po.poNumber})`,
      poNumber: po.poNumber,
    }));
  }, [purchaseOrders]);

  // Vendor options (for display)
  const vendorOptions = useMemo(() => {
    return vendors.map((v) => ({
      value: v._id,
      label: v.companyName || "Unnamed Vendor",
    }));
  }, [vendors]);

  // Contact options (filtered by selected vendor)
  const contactOptions = useMemo(() => {
    return contacts
      .filter((c) => (lockedVendor ? c.relatedAccount === lockedVendor : true))
      .map((c) => ({
        value: c._id,
        label: `${c.firstName} ${c.lastName} (${c.email || "No email"})`,
      }));
  }, [contacts, lockedVendor]);

  // User options for field contacts
  const userOptions = useMemo(() => {
    return users.map((u) => ({
      value: u._id,
      label: `${u.displayName || "Unnamed"} (${u.phoneNumber || "No phone"})`,
    }));
  }, [users]);

  // Handle PO change
  const handlePOChange = useCallback(
    async (selected) => {
      const poId = selected ? selected.value : "";

      if (selectedItems.length > 0) {
        setDialog({
          open: true,
          type: "warning",
          title: "Confirm Purchase Order Change",
          message:
            "Changing the Purchase Order will clear all selected items. Do you want to proceed?",
          onConfirm: async () => {
            await proceedWithPOChange(poId, selected?.poNumber);
            setDialog((prev) => ({ ...prev, open: false }));
          },
          onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      } else {
        await proceedWithPOChange(poId, selected?.poNumber);
      }
    },
    [selectedItems.length],
  );

  const proceedWithPOChange = async (poId, poNumber) => {
    try {
      // Fetch next material order number
      let materialOrderNumber = "";
      if (poId && poNumber) {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE}/materialOrders2/next-number?projectId=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        materialOrderNumber = `${res.data.nextSeq}`;
      }

      setFormData({
        purchaseOrder: poId,
        poNumber: poNumber?.toString() || "",
        vendor: "",
        materialOrderNumber,
        shippingMethod: "Delivery",
        deliveryInstructions: "",
        contact: [],
        fieldContact: [],
        deliveryContact: null,
        expectedDeliveryDate: "",
        laborersRequired: false,
        laborersQty: 0,
      });
      setSelectedItems([]);
      setLockedVendor(null);
      setFilters({
        conditionName: [],
        sectionNumber: [],
        bidArea: [],
        laborId: [],
        size: [],
      });
      setSearchQuery("");
    } catch (err) {
      setError(`Failed to change PO: ${err.message}`);
    }
  };

  // Handle item selection - first item locks the vendor
  const handleItemSelect = useCallback(
    (item) => {
      setSelectedItems((prev) => {
        const existingIndex = prev.findIndex(
          (si) =>
            si.assemblyId === item.assemblyId &&
            si.lineItemId === item.lineItemId,
        );

        if (existingIndex >= 0) {
          // Deselecting
          const newItems = prev.filter((_, i) => i !== existingIndex);

          // If no items left, unlock vendor
          if (newItems.length === 0) {
            setLockedVendor(null);
            setFormData((f) => ({ ...f, vendor: "", contact: [] }));
          }

          return newItems;
        }

        // Selecting new item
        // Check vendor compatibility
        if (lockedVendor && item.vendorId !== lockedVendor) {
          setDialog({
            open: true,
            type: "error",
            title: "Vendor Mismatch",
            message:
              "All items must be from the same vendor. Please create a separate order for items from different vendors.",
            onConfirm: () => setDialog((d) => ({ ...d, open: false })),
          });
          return prev;
        }

        // If first item, lock the vendor
        if (prev.length === 0 && item.vendorId) {
          setLockedVendor(item.vendorId);
          setFormData((f) => ({
            ...f,
            vendor: item.vendorId,
            contact: [],
          }));
        }
        justSelectedRef.current = `${item.assemblyId}-${item.lineItemId}`;
        return [
          ...prev,
          {
            assemblyId: item.assemblyId,
            lineItemId: item.lineItemId,
            quantity: item.isFullyOrdered ? 1 : 0,

            size: item.size || "",
            bidArea: item.bidArea || "",
            price: item.unitPrice || 0,
            maxQty: item.isFullyOrdered ? Infinity : item.qtyRemaining,
            isVarianceOrder: item.isFullyOrdered, // ← track this
            description: item.costCodeDescription || item.itemDescription || "",
          },
        ];
      });
    },
    [lockedVendor],
  );

  // Handle quantity change
  const handleQuantityChange = useCallback((assemblyId, lineItemId, value) => {
    setSelectedItems((prev) =>
      prev.map((si) => {
        if (si.assemblyId === assemblyId && si.lineItemId === lineItemId) {
          const cap =
            si.maxQty === Infinity ? Number.MAX_SAFE_INTEGER : si.maxQty;
          const qty = Math.min(Math.max(0, parseInt(value) || 0), cap);
          return { ...si, quantity: qty };
        }
        return si;
      }),
    );
  }, []);

  // Handle price change
  const handlePriceChange = useCallback((assemblyId, lineItemId, value) => {
    setSelectedItems((prev) =>
      prev.map((si) => {
        if (si.assemblyId === assemblyId && si.lineItemId === lineItemId) {
          return { ...si, price: parseFloat(value) || 0 };
        }
        return si;
      }),
    );
  }, []);

  // Handle bid area change for selected item
  const handleBidAreaChange = useCallback(
    (assemblyId, lineItemId, selected) => {
      setSelectedItems((prev) =>
        prev.map((si) => {
          if (si.assemblyId === assemblyId && si.lineItemId === lineItemId) {
            return { ...si, bidArea: selected?.value || "" };
          }
          return si;
        }),
      );
    },
    [],
  );

  // Handle filter change (multi-select)
  const handleFilterChange = useCallback((filterType, selectedOptions) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: selectedOptions
        ? selectedOptions.map((opt) => opt.value)
        : [],
    }));
  }, []);

  // ─── Multi-token search (shared pattern from searchUtils.js) ─────────────────

  function normaliseSearch(str) {
    if (!str) return "";
    return String(str)
      .toLowerCase()
      .replace(/[''"""]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function buildMOSearchBlob(item) {
    const parts = [
      item.materialCode || item.itemCode || "",
      item.costCodeDescription ||
        item.itemDescription ||
        item.description ||
        "",
      item.conditionName || "",
      item.sectionNumber || "",
      item.size || "",
      item.uom || item.container || "",
      item.bidArea || "",
      item.laborId || "",
      item.vendorName || "",
    ];
    return normaliseSearch(parts.join("  "));
  }

  function moTokenVariants(token) {
    const variants = [token];
    // e.g. "358" → "3-5/8", "3 5/8"
    const fractionCondensed = token.match(/^(\d)(\d)(\d)$/);
    if (fractionCondensed) {
      const [, a, b, c] = fractionCondensed;
      variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`, `${a}${b}/${c}`);
    }
    // e.g. "3516" → "3-5/16"
    const fractionCondensed4 = token.match(/^(\d)(\d)(\d{2})$/);
    if (fractionCondensed4) {
      const [, a, b, c] = fractionCondensed4;
      variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`);
    }
    const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d+)$/);
    if (fractionExpanded) {
      const [, a, b, c] = fractionExpanded;
      variants.push(`${a}${b}${c}`);
    }
    const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
    if (gaugeMatch) {
      const [, num] = gaugeMatch;
      variants.push(`${num}ga`, `${num} ga`, `${num}gauge`);
    }
    return variants;
  }

  function scoreMOItem(item, tokens) {
    let score = 0;
    const fullQuery = tokens.join(" ");
    const code = normaliseSearch(item.materialCode || item.itemCode || "");
    const desc = normaliseSearch(
      item.costCodeDescription ||
        item.itemDescription ||
        item.description ||
        "",
    );
    const cond = normaliseSearch(item.conditionName);
    const size = normaliseSearch(item.size);

    if (code === fullQuery) score += 10000;
    else if (code.startsWith(fullQuery)) score += 5000;
    else if (code.includes(fullQuery)) score += 2000;

    if (desc === fullQuery) score += 8000;
    else if (desc.startsWith(fullQuery)) score += 3000;

    const fields = [
      { text: code, weight: 50 },
      { text: desc, weight: 30 },
      { text: cond, weight: 15 },
      { text: size, weight: 12 },
      { text: normaliseSearch(item.sectionNumber), weight: 10 },
      { text: normaliseSearch(item.laborId), weight: 8 },
      { text: normaliseSearch(item.vendorName), weight: 5 },
      { text: normaliseSearch(item.uom || item.container || ""), weight: 4 },
    ];

    for (const token of tokens) {
      const variants = moTokenVariants(token);
      for (const { text, weight } of fields) {
        if (!text) continue;
        for (const v of variants) {
          if (text === v) {
            score += weight * 3;
            break;
          } else if (text.startsWith(v)) {
            score += weight * 2;
            break;
          } else if (text.includes(v)) {
            score += weight;
            break;
          }
        }
      }
    }

    score -= desc.length * 0.01;
    return score;
  }

  function moMultiTokenSearch(items, searchStr) {
    const raw = normaliseSearch(searchStr);
    if (!raw) return items;
    const tokens = raw.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return items;

    const matched = items.filter((item) => {
      const blob = buildMOSearchBlob(item);
      return tokens.every((token) =>
        moTokenVariants(token).some((v) => blob.includes(v)),
      );
    });

    return matched
      .map((item) => ({ item, score: scoreMOItem(item, tokens) }))
      .sort((a, b) => b.score - a.score)
      .map((e) => e.item);
  }
  // ─────────────────────────────────────────────────────────────────────────────

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({
      conditionName: [],
      sectionNumber: [],
      bidArea: [],
      laborId: [],
      size: [],
    });
    setSearchQuery("");
  }, []);

  // Check if item is selected
  const isItemSelected = useCallback(
    (item) =>
      selectedItems.some(
        (si) =>
          si.assemblyId === item.assemblyId &&
          si.lineItemId === item.lineItemId,
      ),
    [selectedItems],
  );

  // Get selected item data
  const getSelectedItem = useCallback(
    (item) =>
      selectedItems.find(
        (si) =>
          si.assemblyId === item.assemblyId &&
          si.lineItemId === item.lineItemId,
      ),
    [selectedItems],
  );

  // Get user ID from token
  const getUserIdFromToken = useCallback(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.id;
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  // Handle form submission
  const handleSubmit = async () => {
    // ────────────────────────────────────────────────
    // Validation - all shown via AlertDialog
    // ────────────────────────────────────────────────
    if (!formData.purchaseOrder) {
      setDialog({
        open: true,
        type: "error",
        title: "Missing Purchase Order",
        message:
          "Please select a Purchase Order before creating the material order.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (!lockedVendor) {
      setDialog({
        open: true,
        type: "error",
        title: "No Vendor Selected",
        message:
          "Please select at least one item.\nThe first item you select will lock the vendor for this order.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (selectedItems.length === 0) {
      setDialog({
        open: true,
        type: "error",
        title: "No Items Selected",
        message: "Please select at least one material item to order.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (selectedItems.some((si) => si.quantity <= 0)) {
      setDialog({
        open: true,
        type: "error",
        title: "Invalid Quantities",
        message: "All selected items must have a quantity greater than 0.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (selectedItems.some((si) => !si.bidArea)) {
      setDialog({
        open: true,
        type: "error",
        title: "Missing Bid Area",
        message: "Every selected item must have a bid area assigned.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    // ────────────────────────────────────────────────
    // Proceed with submission
    // ────────────────────────────────────────────────
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const userId = getUserIdFromToken();

      // Helper functions (unchanged)
      const calculateCoverageFromSize = (size, uom) => {
        if (!size) return 1;
        const uomLower = (uom || "").toLowerCase();
        const isSF =
          uomLower.includes("sf") ||
          uomLower.includes("square") ||
          uomLower.includes("sq");
        const isLF =
          uomLower === "lf" ||
          uomLower.includes("linear") ||
          uomLower.includes("lin ft") ||
          uomLower.includes("lin.ft") ||
          uomLower.includes("lin");

        // Only calculate coverage for SF or LF UOMs
        if (!isSF && !isLF) return 1;

        const parseToFeet = (s) => {
          s = String(s).trim();

          // Helper: parse a value that may contain a fraction like "4-3/4" or "3/4" or "4.5"
          const parseWithFraction = (str) => {
            str = String(str).trim().replace(/"$/, "");
            // Mixed number: "4-3/4" or "4 3/4"
            const mixed = str.match(/^([\d.]+)[\s-]([\d.]+)\/([\d.]+)$/);
            if (mixed)
              return (
                parseFloat(mixed[1]) +
                parseFloat(mixed[2]) / parseFloat(mixed[3])
              );
            // Pure fraction: "3/4"
            const frac = str.match(/^([\d.]+)\/([\d.]+)$/);
            if (frac) return parseFloat(frac[1]) / parseFloat(frac[2]);
            // Plain number
            return parseFloat(str) || 0;
          };

          // Pattern: feet'inches" where inches may have fractions — e.g. 10' 4-3/4"
          let m = s.match(/^([\d.]+)'[\s]*([\d]+[\s-][\d]+\/[\d]+)"?$/);
          if (m) return parseFloat(m[1]) + parseWithFraction(m[2]) / 12;

          // Pattern: feet'inches" where inches is a simple fraction — e.g. 10' 3/4"
          m = s.match(/^([\d.]+)'[\s]*([\d]+\/[\d]+)"?$/);
          if (m) return parseFloat(m[1]) + parseWithFraction(m[2]) / 12;

          // Pattern: feet'inches" where inches is a decimal — e.g. 10'4" or 10' 4.5"
          m = s.match(/^([\d.]+)'[\s]*([\d.]+)"?$/);
          if (m) return parseFloat(m[1]) + (parseFloat(m[2]) || 0) / 12;

          // Pattern: feet' inches" with space — e.g. 10' 4"
          m = s.match(/^([\d.]+)'?\s+([\d.]+)"?$/);
          if (m) return parseFloat(m[1]) + (parseFloat(m[2]) || 0) / 12;

          // Just feet — e.g. 10'
          if (s.includes("'") && !s.includes('"'))
            return parseFloat(s.replace("'", "")) || 0;

          // Just inches — e.g. 4-3/4" or 4.75"
          if (s.endsWith('"') && !s.includes("'"))
            return parseWithFraction(s.slice(0, -1)) / 12;

          return parseFloat(s) || 0;
        };

        if (isSF) {
          const parts = String(size)
            .replace(/×/g, "x")
            .split(/\s*[xX]\s*/);
          if (parts.length === 2) {
            const width = parseToFeet(parts[0]);
            const height = parseToFeet(parts[1]);
            if (width > 0 && height > 0) {
              return Number((width * height).toFixed(2));
            }
          }
        }

        if (isLF) {
          const length = parseToFeet(size);
          if (length > 0) return Number(length.toFixed(2));
        }

        return 1;
      };

      const getCoverage = (item) => {
        if (!item) return 1;
        const currentSize = item.size || "";
        const calculated = calculateCoverageFromSize(currentSize, item.uom);
        if (calculated > 0) return calculated;

        if (
          item.coveragePerUnit !== undefined &&
          item.coveragePerUnit !== null &&
          item.coveragePerUnit !== ""
        ) {
          const parsed = parseFloat(item.coveragePerUnit);
          if (!isNaN(parsed) && parsed > 0) return parsed;
        }
        return 1;
      };

      const safeNumber = (val, defaultVal = 0) => {
        if (val === undefined || val === null || val === "") return defaultVal;
        const parsed = parseFloat(val);
        return isNaN(parsed) ? defaultVal : parsed;
      };

      const orderData = {
        project: id,
        purchaseOrder: formData.purchaseOrder,
        vendor: lockedVendor,
        poNumber: formData.poNumber,
        materialOrderNumber: formData.materialOrderNumber,
        shippingMethod: formData.shippingMethod,
        deliveryInstructions: formData.deliveryInstructions,
        contact: formData.contact,
        fieldContact: formData.fieldContact,
        deliveryContact: formData.deliveryContact,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        laborersRequired: formData.laborersRequired,
        laborersQty: formData.laborersRequired ? formData.laborersQty : 0,
        createdBy: userId,
        items: selectedItems.map((si) => {
          const originalItem =
            poItems.find(
              (item) =>
                item.assemblyId === si.assemblyId &&
                item.lineItemId === si.lineItemId,
            ) || {};

          const itemForCoverage = {
            size: si.size || originalItem.size || "",
            uom: originalItem.uom || originalItem.container || "",
            coveragePerUnit: originalItem.coveragePerUnit || "",
          };

          const coverage = getCoverage(itemForCoverage);

          const unitPrice = si.price || 0;

          return {
            assembly: si.assemblyId,
            lineItemId: si.lineItemId,
            quantity: si.quantity,
            size: si.size || "",
            bidArea: si.bidArea || "",
            price: Number(unitPrice.toFixed(4)),
            coveragePerUnit: coverage,
          };
        }),
      };

      const res = await axios.post(`${API_BASE}/materialOrders2`, orderData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMaterialOrders((prev) => [...prev, res.data.materialOrder]);

      // Reset form
      setFormData({
        purchaseOrder: "",
        vendor: "",
        poNumber: "",
        shippingMethod: "Delivery",
        deliveryInstructions: "",
        materialOrderNumber: "",
        contact: [],
        fieldContact: [],
        deliveryContact: null,
        expectedDeliveryDate: "",
        laborersRequired: false,
        laborersQty: 0,
      });
      setSelectedItems([]);
      setLockedVendor(null);
      setPoItems([]);

      // Optional: show success dialog before navigation
      // At the end of successful creation (after reset)
      setDialog({
        open: true,
        type: "success",
        title: "Material Order Created",
        message: `Order was successfully created.`,
        confirmText: "OK",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          navigate(`/Projects/${id}`, { state: { initialTab: "Materials" } });
        },
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Failed to Create Order",
        message:
          err.response?.data?.message ||
          err.message ||
          "An unexpected error occurred while creating the material order. Please try again.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show confirmation dialog
  const showConfirmationDialog = (e) => {
    e.preventDefault();

    setDialog({
      open: true,
      type: "warning",
      title: "Confirm Material Order",
      message: "Are you sure you want to create this material order?",
      confirmText: "Create Order",
      cancelText: "Cancel",
      onConfirm: async () => {
        // Show loading state in the current dialog
        setDialog((prev) => ({
          ...prev,
          message: "Creating material order... Please wait.",
          confirmText: "Creating...",
          cancelText: "", // hide cancel button during submit
        }));

        // Run submission — let it decide what to do
        await handleSubmit();

        // DO NOT close here anymore
        // handleSubmit will either:
        // - navigate away on success (dialog disappears naturally)
        // - show error dialog by setting new state (replaces current dialog)
        // - do nothing if validation failed early
      },
      onClose: () => setDialog((prev) => ({ ...prev, open: false })),
      onCancel: () => setDialog((prev) => ({ ...prev, open: false })),
    });
  };

  // Add this useMemo hook after the `totalCost` useMemo (around line 465)

  // Group selected items by unique item characteristics and sum quantities
  const itemTotals = useMemo(() => {
    const totals = new Map();

    selectedItems.forEach((si) => {
      // Create a unique key based on description and size
      const key = `${si.description}|${si.size}`;

      if (totals.has(key)) {
        const existing = totals.get(key);
        existing.totalQty += si.quantity;
        existing.totalCost += si.quantity * si.price;
        existing.assemblies.add(si.assemblyId);
      } else {
        totals.set(key, {
          description: si.description,
          size: si.size,
          totalQty: si.quantity,
          totalCost: si.quantity * si.price,
          price: si.price,
          assemblies: new Set([si.assemblyId]),
          bidArea: si.bidArea,
        });
      }
    });

    // Convert to array and sort by description
    return Array.from(totals.values()).sort((a, b) =>
      a.description.localeCompare(b.description),
    );
  }, [selectedItems]);

  // Helper function to format the item totals display
  const formatItemTotal = useCallback((item) => {
    const assemblyCount = item.assemblies.size;
    const assemblyText =
      assemblyCount > 1 ? ` (${assemblyCount} assemblies)` : "";
    return `${item.description} ${item.size !== "-" ? `- ${item.size}` : ""}${assemblyText}`;
  }, []);

  // Format currency
  const formatCurrency = useCallback((num) => {
    return Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-200">Loading...</div>
      </div>
    );
  }

  return (
    <div className="ml-4 mx-auto px-4 py-6">
      <AlertDialog
        open={dialog.open}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="Confirm"
        cancelText="Cancel"
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={dialog.onConfirm}
      />

      {/* Header */}
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">
              Create Material Order
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Select a PO, then choose items to order. First item selected
              determines the vendor.
            </p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowOrders(!showOrders)}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
            >
              {showOrders ? "Hide Orders" : "View Orders"}
            </button>
            <button
              onClick={() =>
                navigate(`/Projects/${id}`, {
                  state: { initialTab: "Materials" },
                })
              }
              className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
            >
              Back
            </button>
          </div>
        </div>
      </div>

      {!showOrders ? (
        <form onSubmit={showConfirmationDialog} className="space-y-6">
          {/* Order Details */}
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-4">
              Order Details
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Purchase Order *
                </label>
                <Select
                  options={poOptions}
                  value={
                    poOptions.find((o) => o.value === formData.purchaseOrder) ||
                    null
                  }
                  onChange={handlePOChange}
                  placeholder="Select Purchase Order..."
                  className="mt-1"
                  styles={customStyles}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Vendor (Auto-set by first item)
                </label>
                <Select
                  options={vendorOptions}
                  value={
                    vendorOptions.find((o) => o.value === lockedVendor) || null
                  }
                  placeholder="Select items to set vendor..."
                  className="mt-1"
                  styles={customStyles}
                  isDisabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Material Order Number
                </label>
                <input
                  type="text"
                  value={formData.materialOrderNumber}
                  readOnly
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Shipping Method
                </label>
                <select
                  value={formData.shippingMethod}
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      shippingMethod: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                >
                  <option value="Delivery">Delivery</option>
                  <option value="Pickup">Pickup</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Expected Delivery Date
                </label>
                <input
                  type="date"
                  value={formData.expectedDeliveryDate}
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      expectedDeliveryDate: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.laborersRequired}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        laborersRequired: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-400">
                    Laborers Required
                  </label>
                </div>
                {formData.laborersRequired && (
                  <input
                    type="number"
                    value={formData.laborersQty}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        laborersQty: parseInt(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    placeholder="# of laborers"
                    className="w-24 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Vendor Contacts
                </label>
                <Select
                  options={contactOptions}
                  value={contactOptions.filter((o) =>
                    formData.contact.includes(o.value),
                  )}
                  onChange={(opts) =>
                    setFormData((f) => ({
                      ...f,
                      contact: opts ? opts.map((o) => o.value) : [],
                    }))
                  }
                  placeholder="Select Contacts..."
                  className="mt-1"
                  styles={customStyles}
                  isMulti
                  isDisabled={!lockedVendor}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Field Contacts
                </label>
                <Select
                  options={userOptions}
                  value={userOptions.filter((o) =>
                    formData.fieldContact.includes(o.value),
                  )}
                  onChange={(opts) =>
                    setFormData((f) => ({
                      ...f,
                      fieldContact: opts ? opts.map((o) => o.value) : [],
                    }))
                  }
                  placeholder="Select Field Contacts..."
                  className="mt-1"
                  styles={customStyles}
                  isMulti
                />
              </div>
              {formData.shippingMethod === "Pickup" && (
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Delivery Driver
                  </label>
                  <Select
                    options={userOptions}
                    value={
                      userOptions.find(
                        (o) => o.value === formData.deliveryContact,
                      ) || null
                    }
                    onChange={(opt) =>
                      setFormData((f) => ({
                        ...f,
                        deliveryContact: opt?.value || null,
                      }))
                    }
                    placeholder="Select Driver..."
                    className="mt-1"
                    styles={customStyles}
                    isClearable
                  />
                </div>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-400">
                Delivery Instructions
              </label>
              <textarea
                value={formData.deliveryInstructions}
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    deliveryInstructions: e.target.value,
                  }))
                }
                placeholder="Enter delivery instructions..."
                rows={2}
                className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Materials Selection */}
          <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-4">
              Select Materials to Order
            </h2>

            {formData.purchaseOrder && (
              <>
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Search
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search description..."
                      className="mt-1 w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Condition
                    </label>
                    <Select
                      options={filterOptions.conditionName}
                      value={filterOptions.conditionName.filter((o) =>
                        filters.conditionName.includes(o.value),
                      )}
                      onChange={(opts) =>
                        handleFilterChange("conditionName", opts)
                      }
                      placeholder="All Conditions..."
                      isMulti
                      isClearable
                      className="mt-1"
                      styles={customStyles}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Section
                    </label>
                    <Select
                      options={filterOptions.sectionNumber}
                      value={filterOptions.sectionNumber.filter((o) =>
                        filters.sectionNumber.includes(o.value),
                      )}
                      onChange={(opts) =>
                        handleFilterChange("sectionNumber", opts)
                      }
                      placeholder="All Sections..."
                      isMulti
                      isClearable
                      className="mt-1"
                      styles={customStyles}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Bid Area
                    </label>
                    <Select
                      options={filterOptions.bidArea}
                      value={filterOptions.bidArea.filter((o) =>
                        filters.bidArea.includes(o.value),
                      )}
                      onChange={(opts) => handleFilterChange("bidArea", opts)}
                      placeholder="All Bid Areas..."
                      isMulti
                      isClearable
                      className="mt-1"
                      styles={customStyles}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Labor ID
                    </label>
                    <Select
                      options={filterOptions.laborId}
                      value={filterOptions.laborId.filter((o) =>
                        filters.laborId.includes(o.value),
                      )}
                      onChange={(opts) => handleFilterChange("laborId", opts)}
                      placeholder="All Labor IDs..."
                      isMulti
                      isClearable
                      className="mt-1"
                      styles={customStyles}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Size
                    </label>
                    <Select
                      options={filterOptions.size}
                      value={filterOptions.size.filter((o) =>
                        filters.size.includes(o.value),
                      )}
                      onChange={(opts) => handleFilterChange("size", opts)}
                      placeholder="All Sizes..."
                      isMulti
                      isClearable
                      className="mt-1"
                      styles={customStyles}
                    />
                  </div>
                </div>

                {/* Filter actions */}
                <div className="mb-4 flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                  >
                    Clear Filters
                  </button>
                  <span className="text-gray-400 text-sm">
                    {filteredItems.length} items shown • {selectedItems.length}{" "}
                    selected
                    {lockedVendor && (
                      <span className="ml-2 text-orange-400">
                        • Vendor:{" "}
                        {
                          vendors.find((v) => v._id === lockedVendor)
                            ?.companyName
                        }
                      </span>
                    )}
                  </span>
                </div>
              </>
            )}

            {/* Item Totals Summary */}
            {selectedItems.length > 0 && (
              <div className="mb-4 bg-gray-800 rounded-lg border border-gray-600 p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-orange-400">
                    Selected Items Summary
                  </h3>
                  <span className="text-xs text-gray-400">
                    {itemTotals.length} unique item
                    {itemTotals.length !== 1 ? "s" : ""}
                  </span>
                </div>

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {itemTotals.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center py-2 px-3 bg-gray-700 rounded text-sm border border-gray-600"
                    >
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-white truncate"
                          title={formatItemTotal(item)}
                        >
                          {formatItemTotal(item)}
                        </p>
                        {item.bidArea && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Bid Area: {item.bidArea}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 ml-4">
                        <div className="text-right">
                          <p className="text-blue-400 font-medium">
                            {item.totalQty}{" "}
                            {item.totalQty === 1 ? "unit" : "units"}
                          </p>
                          {userRole !== "foreman" && (
                            <p className="text-xs text-gray-400">
                              @{" "}
                              <NumericFormat
                                value={item.price}
                                displayType="text"
                                thousandSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$"
                              />{" "}
                              ea
                            </p>
                          )}
                        </div>
                        {userRole !== "foreman" && (
                          <div className="text-right min-w-[100px]">
                            <NumericFormat
                              value={item.totalCost}
                              displayType="text"
                              thousandSeparator=","
                              decimalScale={2}
                              fixedDecimalScale
                              prefix="$"
                              className="text-orange-400 font-medium"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {userRole !== "foreman" && (
                  <div className="mt-3 pt-3 border-t border-gray-600 flex justify-between items-center">
                    <span className="text-sm text-gray-400">Grand Total:</span>
                    <NumericFormat
                      value={totalCost}
                      displayType="text"
                      thousandSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                      prefix="$"
                      className="text-lg font-semibold text-orange-400"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Items Table */}
            {!formData.purchaseOrder ? (
              <p className="text-gray-400">
                Select a Purchase Order to see available items.
              </p>
            ) : filteredItems.length === 0 ? (
              <p className="text-gray-400">
                No materials available to order
                {lockedVendor && " from this vendor"}.
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedItems).map(([conditionName, items]) => (
                  <div
                    key={conditionName}
                    className="border border-gray-600 rounded-lg overflow-hidden"
                  >
                    {/* Condition Header */}
                    <div className="bg-gray-800 px-4 py-2 flex justify-between items-center">
                      <span className="text-white font-medium">
                        {conditionName}
                      </span>
                      <span className="text-gray-400 text-sm">
                        {items.length} items
                      </span>
                    </div>

                    {/* Items Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                        <thead className="bg-gray-750">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-10"></th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Section
                            </th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Labor ID
                            </th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Description
                            </th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Size
                            </th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Vendor
                            </th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Req
                            </th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Pending
                            </th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Ordered
                            </th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Avail
                            </th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Qty to Order
                            </th>
                            {userRole !== "foreman" && (
                              <>
                                <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                                  Unit Price
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                                  Line Total
                                </th>
                                <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                                  Budget Amt
                                </th>
                              </>
                            )}
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                              Bid Area
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-600">
                          {items.map((item) => {
                            const selected = isItemSelected(item);
                            const selectedData = getSelectedItem(item);
                            const isDisabledByVendor =
                              lockedVendor && item.vendorId !== lockedVendor;
                            const lineTotal = selectedData
                              ? selectedData.quantity * selectedData.price
                              : 0;

                            return (
                              <tr
                                key={`${item.assemblyId}-${item.lineItemId}`}
                                onClick={(e) => {
                                  if (isDisabledByVendor) return;

                                  // Only trigger selection when clicking the checkbox cell or the checkbox itself
                                  const checkboxTd =
                                    e.currentTarget.querySelector(
                                      "td:first-child",
                                    );
                                  if (!checkboxTd?.contains(e.target)) return;

                                  if (
                                    e.target.tagName === "INPUT" ||
                                    e.target.tagName === "SELECT" ||
                                    e.target.tagName === "BUTTON" ||
                                    e.target.closest("input, select, button")
                                  ) {
                                    return;
                                  }

                                  handleItemSelect(item);
                                }}
                                className={`
  cursor-default transition-colors duration-150
  ${
    selected
      ? "bg-orange-900/30 border-l-4 border-l-orange-500"
      : "hover:bg-gray-600/70"
  }
  ${isDisabledByVendor ? "opacity-50 bg-gray-800/60 cursor-not-allowed" : ""}
`}
                              >
                                <td className="px-2 py-2 whitespace-nowrap text-sm cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => handleItemSelect(item)}
                                    disabled={isDisabledByVendor}
                                    className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                                  />
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                                  {item.sectionNumber || "-"}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-300">
                                  {item.laborId || "-"}
                                </td>
                                <td
                                  className="px-2 py-3 text-sm text-gray-200 max-w-[200px]"
                                  title={
                                    item.costCodeDescription ||
                                    item.itemDescription
                                  } // still nice for hover
                                >
                                  {item.costCodeDescription ||
                                    item.itemDescription ||
                                    "-"}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                                  {item.size || "-"}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                                  {item.vendorName || "-"}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-white text-right">
                                  {item.requiredQty}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-yellow-400 text-right">
                                  {item.qtyPending || "-"}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-green-400 text-right">
                                  {item.qtyOrdered || "-"}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-right font-medium">
                                  <span
                                    className={
                                      item.isFullyOrdered
                                        ? "text-orange-400 font-bold"
                                        : "text-blue-400"
                                    }
                                  >
                                    {item.qtyRemaining}
                                  </span>
                                </td>
                                <td
                                  className="px-2 py-2 whitespace-nowrap text-sm text-right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={selectedData?.quantity ?? ""}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      if (!selected) return;
                                      const val = e.target.value.replace(
                                        /\D/g,
                                        "",
                                      );
                                      handleQuantityChange(
                                        item.assemblyId,
                                        item.lineItemId,
                                        val === "" ? 0 : val,
                                      );
                                    }}
                                    onBlur={(e) => {
                                      if (!selected) return;
                                      const num = Math.max(
                                        0,
                                        Number(e.target.value) || 0,
                                      );
                                      const rawMax =
                                        selectedData?.maxQty ??
                                        item.qtyRemaining ??
                                        9999;
                                      const max =
                                        rawMax === Infinity ||
                                        (rawMax === 0 && item.isFullyOrdered)
                                          ? Number.MAX_SAFE_INTEGER
                                          : rawMax || 9999;
                                      handleQuantityChange(
                                        item.assemblyId,
                                        item.lineItemId,
                                        Math.min(num, max),
                                      );
                                    }}
                                    onFocus={(e) => {
                                      if (!selected) {
                                        // Clicking the input on an unselected row should select the row first
                                        handleItemSelect(item);
                                        return;
                                      }
                                      e.target.select();
                                    }}
                                    ref={(el) => {
                                      const key = `${item.assemblyId}-${item.lineItemId}`;
                                      qtyInputRefs.current[key] = el;
                                      if (
                                        el &&
                                        justSelectedRef.current === key
                                      ) {
                                        justSelectedRef.current = null;
                                        setTimeout(() => {
                                          el.focus();
                                          el.select();
                                        }, 0);
                                      }
                                    }}
                                    placeholder="—"
                                    className={`w-20 px-2 py-1 text-center text-sm font-medium border rounded-md
    focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none
    [appearance:textfield] [&::-webkit-outer-spin-button]:hidden [&::-webkit-inner-spin-button]:hidden
    transition-colors duration-150
    ${
      selected
        ? "bg-gray-800 border-gray-600 text-white cursor-text"
        : "bg-transparent border-transparent text-gray-400 cursor-pointer"
    }`}
                                  />
                                </td>
                                {userRole !== "foreman" && (
                                  <>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right">
                                      <NumericFormat
                                        value={
                                          selectedData?.price ??
                                          item.unitPrice ??
                                          0
                                        }
                                        displayType="text"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        prefix="$"
                                        className="text-white"
                                      />
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right">
                                      <NumericFormat
                                        value={lineTotal}
                                        displayType="text"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        prefix="$"
                                        className="text-white"
                                      />
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-400">
                                      <NumericFormat
                                        value={item.totalAmount || 0}
                                        displayType="text"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        prefix="$"
                                      />
                                    </td>
                                  </>
                                )}
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-400">
                                  {item.bidArea}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Total Cost */}
            {userRole !== "foreman" && selectedItems.length > 0 && (
              <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                <p className="text-lg font-semibold text-gray-200">
                  Total Cost:{" "}
                  <NumericFormat
                    value={totalCost}
                    displayType="text"
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    className="text-orange-400"
                  />
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setSelectedItems([]);
                  setLockedVendor(null);
                  setFormData({
                    purchaseOrder: "",
                    vendor: "",
                    poNumber: "",
                    shippingMethod: "Delivery",
                    deliveryInstructions: "",
                    materialOrderNumber: "",
                    contact: [],
                    fieldContact: [],
                    deliveryContact: null,
                    expectedDeliveryDate: "",
                    laborersRequired: false,
                    laborersQty: 0,
                  });
                  navigate(`/Projects/${id}`, {
                    state: { initialTab: "Materials" },
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || selectedItems.length === 0}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                  isSubmitting || selectedItems.length === 0
                    ? "bg-orange-300 cursor-not-allowed"
                    : "bg-orange-400 hover:bg-orange-500"
                }`}
              >
                {isSubmitting ? "Creating..." : "Create Order"}
              </button>
            </div>
          </div>
        </form>
      ) : (
        /* Material Orders List */
        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-orange-400 mb-4">
            Material Orders
          </h2>
          {materialOrders.length === 0 ? (
            <p className="text-gray-400">No material orders found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      MO #
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      PO #
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      Vendor
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      Shipping
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      Expected
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {materialOrders.map((order) => {
                    const vendor = vendors.find(
                      (v) => v._id === (order.vendor?._id || order.vendor),
                    );
                    return (
                      <tr
                        key={order._id}
                        className="hover:bg-gray-600 transition-colors"
                      >
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                          {order.materialOrderNumber}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {order.poNumber}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {vendor?.companyName || "Unknown"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {order.shippingMethod}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              order.status === "pending"
                                ? "bg-yellow-600/20 text-yellow-400"
                                : order.status === "confirmed"
                                  ? "bg-blue-600/20 text-blue-400"
                                  : order.status === "delivered"
                                    ? "bg-green-600/20 text-green-400"
                                    : "bg-red-600/20 text-red-400"
                            }`}
                          >
                            {order.status?.charAt(0).toUpperCase() +
                              order.status?.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {order.expectedDeliveryDate
                            ? new Date(
                                order.expectedDeliveryDate,
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                          <button
                            onClick={() =>
                              navigate(
                                `/projects/${id}/material-order/${order._id}`,
                              )
                            }
                            className="text-orange-400 hover:text-orange-300"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
